import {map} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  ViewChild,
} from '@angular/core';
import {ViewTabComponent} from '../../viewtab.component';
import {ErrorService} from '../../../../error/error.service';
import {Observable} from 'rxjs';
import {CacheService} from '../../../../cache/cache.service';
import {ResourceService} from '../../../resource.service';
import {EnvironmentConfigService} from '../../../../core/services/environment-config.service';
import {MatDialog} from '@angular/material/dialog';
import {LanguageService} from '../../../../language/language.service';
import {AbstractAttributeFlowControl} from './abstract-attribute-flow-control';
import {ExportFlow} from './attribute-flow';

@Component({
  selector: 'ctrl-initial-attribute-flow',
  styleUrls: ['./ctrl-synchronization.scss'],
  template: `
    <ctrl-attribute-flow-title
      [title]="getCaughtObservable(listViewTitle)"
      [control]="formControl"
      [enabled]="getCaughtObservable(isEnabled)"
    ></ctrl-attribute-flow-title>
    <table class="table mat-elevation-z8">
      <thead>
        <tr>
          <th>
            <language
              key="CtrlInitialAttributeFlowColumnSource"
              defaultValue="Flow Source: FIM Attribute"
            ></language>
          </th>
          <th>
            <language
              key="CtrlInitialAttributeFlowColumnDestination"
              defaultValue="Flow Destination: External System Attributes/Values"
            ></language>
          </th>
          <th>
            <language key="CtrlInitialAttributeFlowAllowsNull" defaultValue="Allow Null"></language>
          </th>
          <th class="buttons">
            <button
              mat-icon-button
              (click)="addRow()"
              color="accent"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              <mat-icon arial-label="Add">add</mat-icon>
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        @for (entry of value; track entry; let i = $index) {
          <tr class="entry">
            <td>
              @if ((getCaughtObservable(isEnabled) | async) != true) {
                <mat-chip-set>
                  @for (src of entry.src; track src) {
                    <mat-chip-row [removable]="false" [class.clickable]="false">
                      {{ src.summary }}
                    </mat-chip-row>
                  }
                </mat-chip-set>
              }
              <mat-form-field [hidden]="(getCaughtObservable(isEnabled) | async) != true">
                <mat-chip-grid #chipList>
                  @for (src of entry.src; track src; let srcI = $index) {
                    <mat-chip-row
                      [removable]="true"
                      (removed)="srcRemoved(entry, srcI)"
                      (click)="srcClick(src)"
                      [class.clickable]="srcClickable(src)"
                    >
                      {{ src.summary }}
                      <mat-icon matChipRemove>cancel</mat-icon>
                    </mat-chip-row>
                  }
                  <input
                    #srcInput
                    [placeholder]="addSourcePlaceholder | async"
                    [formControl]="srcCtrl"
                    [matAutocomplete]="srcAuto"
                    [matChipInputFor]="chipList"
                    [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                  />
                </mat-chip-grid>
                <mat-autocomplete
                  #srcAuto="matAutocomplete"
                  (optionSelected)="srcSelected(entry, $event, srcInput)"
                >
                  @for (optGrp of filteredSourceOptions | async; track optGrp) {
                    <mat-optgroup [label]="optGrp.group">
                      @for (opt of optGrp.sources; track opt) {
                        <mat-option [value]="opt">
                          {{ opt.displayName }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }
                </mat-autocomplete>
              </mat-form-field>
            </td>
            <td>
              <attribute-flow-destination
                [placeholder]="destAttributePlaceholder | async"
                [value]="entry.dest"
                (valueChange)="entry.dest = $event; formControl.updateValueAndValidity()"
                (onError)="handleError($event.err, $event.caught)"
                [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                [options]="csAttributes"
              ></attribute-flow-destination>
            </td>
            <td>
              <mat-checkbox
                [(ngModel)]="entry.allowsNull"
                [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                (change)="formControl.updateValueAndValidity()"
              ></mat-checkbox>
            </td>
            <td class="buttons">
              <button
                mat-icon-button
                (click)="delete(i)"
                [disabled]="(getCaughtObservable(isEnabled) | async) != true"
              >
                <mat-icon aria-label="Delete">close</mat-icon>
              </button>
            </td>
          </tr>
        }
        <tr>
          <td></td>
          <td></td>
          <td></td>
          <th class="buttons">
            <button
              mat-icon-button
              (click)="addRow()"
              color="accent"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              <mat-icon arial-label="Add">add</mat-icon>
            </button>
          </th>
        </tr>
      </tbody>
    </table>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlInitialAttributeFlowComponent extends AbstractAttributeFlowControl {
  constructor(
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    cacheService: CacheService,
    resourceService: ResourceService,
    dialog: MatDialog,
    envConfigService: EnvironmentConfigService,
  ) {
    super(
      parent,
      'InitialFlow',
      'ilm',
      ExportFlow,
      changeDetector,
      _ngZone,
      errorService,
      cacheService,
      resourceService,
      dialog,
      envConfigService,
    );

    this.addSourcePlaceholder = cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'CtrlInitialAttributeFlowAddSourcePlaceholder',
          'Add source…',
          lr,
        );
      }),
    );
    this.destAttributePlaceholder = cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'CtrlInitialAttributeFlowSelectDestinationPlaceholder',
          'Please select an attribute.',
          lr,
        );
      }),
    );
  }

  addSourcePlaceholder: Observable<string>;
  destAttributePlaceholder: Observable<string>;

  @ViewChild('srcInput') srcInput: ElementRef<HTMLInputElement>;
}
